import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, DatePicker, Input, InputNumber, Select, Space, Table, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { OPS_TOOL_SETTLEMENT_AUDIT_LOGS } from './graphql/opsToolSettlementGraphql';

dayjs.extend(utc);

type AuditLogRow = {
  id: string;
  operatorId: string;
  operatorName: string;
  module: string;
  moduleName: string;
  operationType: string;
  operationTypeName: string;
  businessId: string | null;
  detailJson: string | null;
  operatedAt: string | null;
};

const MODULE_OPTIONS = [
  { value: 'BILL', label: '账单' },
  { value: 'ADJUSTMENT', label: '调账' },
  { value: 'REALTIME_SETTLEMENT', label: '实时结算' },
  { value: 'PRICING', label: '定价' },
  { value: 'CLEARING', label: '清分' },
  { value: 'SETTLEMENT', label: '结算' },
];

const OPERATION_TYPE_OPTIONS = [
  { value: 'ACCOUNT_ADJUSTMENT', label: '账户调账' },
  { value: 'OVERDRAFT_LIMIT_UPDATE', label: '透支额度修改' },
  { value: 'PRICING_SCHEME_CREATE', label: '定价方案新建' },
  { value: 'PRICING_SCHEME_UPDATE', label: '定价方案修改' },
  { value: 'PRICING_SCHEME_DELETE', label: '定价方案删除' },
  { value: 'PRICING_SCHEME_BIND', label: '定价方案绑定' },
  { value: 'PRICING_SCHEME_UNBIND', label: '定价方案解绑' },
  { value: 'PRICING_SCHEME_APPEND_BIND', label: '定价方案追加绑定' },
  { value: 'CHANNEL_SETTLEMENT_SAVE', label: '渠道结算保存' },
  { value: 'DSP_SETTLEMENT_SAVE', label: 'DSP结算保存' },
];

const formatJsonDetail = (raw: string | null): string => {
  if (!raw) return '-';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

const SettlementAuditLogPage: React.FC = () => {
  const [operatorId, setOperatorId] = useState<number | undefined>();
  const [operatorName, setOperatorName] = useState<string | undefined>();
  const [moduleValue, setModuleValue] = useState<string | undefined>();
  const [operationType, setOperationType] = useState<string | undefined>();
  const [businessId, setBusinessId] = useState<string | undefined>();
  const [operatedRange, setOperatedRange] = useState<[Dayjs | null, Dayjs | null] | null>([
    dayjs().subtract(6, 'day'),
    dayjs(),
  ]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [loadLogs, { data, loading, refetch }] = useLazyQuery(OPS_TOOL_SETTLEMENT_AUDIT_LOGS);

  const buildFilters = useCallback(() => {
    const operatedAtFrom = operatedRange?.[0]
      ? dayjs(operatedRange[0]).startOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
      : undefined;
    const operatedAtTo = operatedRange?.[1]
      ? dayjs(operatedRange[1]).endOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
      : undefined;
    return {
      operatorId: operatorId ?? null,
      operatorName: operatorName?.trim() || null,
      module: moduleValue ?? null,
      operationType: operationType ?? null,
      businessId: businessId?.trim() || null,
      operatedAtFrom,
      operatedAtTo,
    };
  }, [operatorId, operatorName, moduleValue, operationType, businessId, operatedRange]);

  const fetchLogs = useCallback(
    (targetPage = page, targetSize = pageSize) => {
      loadLogs({
        variables: {
          filters: buildFilters(),
          pageRequest: { page: targetPage, size: targetSize },
        },
      });
    },
    [loadLogs, buildFilters, page, pageSize]
  );

  useEffect(() => {
    fetchLogs(1, pageSize);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pageResult = data?.opsToolSettlementAuditLogs;
  const rows: AuditLogRow[] = useMemo(
    () =>
      (pageResult?.content ?? []).map((row: AuditLogRow) => ({
        ...row,
        id: String(row.id),
        operatorId: String(row.operatorId),
        businessId: row.businessId != null ? String(row.businessId) : null,
      })),
    [pageResult]
  );

  useEffect(() => {
    if (pageResult && !pageResult.success) {
      message.error(pageResult.message || '查询审计日志失败');
    }
  }, [pageResult]);

  const columns: ColumnsType<AuditLogRow> = [
    { title: '操作时间(UTC)', dataIndex: 'operatedAt', width: 200 },
    { title: '操作人', dataIndex: 'operatorName', width: 120 },
    { title: '操作人ID', dataIndex: 'operatorId', width: 100 },
    { title: '模块', dataIndex: 'moduleName', width: 100 },
    { title: '操作类型', dataIndex: 'operationTypeName', width: 140 },
    {
      title: '核心业务ID',
      dataIndex: 'businessId',
      width: 140,
      render: (v: string | null) => v ?? '-',
    },
    {
      title: '操作详情',
      dataIndex: 'detailJson',
      ellipsis: true,
      render: (v: string | null) => v ?? '-',
    },
  ];

  const handleSearch = () => {
    setPage(1);
    fetchLogs(1, pageSize);
  };

  return (
    <Card title="结算审计日志" bordered={false}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Space wrap>
          <InputNumber
            placeholder="操作人ID"
            style={{ width: 140 }}
            value={operatorId}
            onChange={value => setOperatorId(value ?? undefined)}
          />
          <Input
            allowClear
            placeholder="操作人姓名"
            style={{ width: 160 }}
            value={operatorName}
            onChange={e => setOperatorName(e.target.value)}
          />
          <Select
            allowClear
            placeholder="模块"
            style={{ width: 140 }}
            options={MODULE_OPTIONS}
            value={moduleValue}
            onChange={setModuleValue}
          />
          <Select
            allowClear
            placeholder="操作类型"
            style={{ width: 180 }}
            options={OPERATION_TYPE_OPTIONS}
            value={operationType}
            onChange={setOperationType}
          />
          <Input
            allowClear
            placeholder="核心业务ID"
            style={{ width: 160 }}
            value={businessId}
            onChange={e => setBusinessId(e.target.value)}
          />
          <DatePicker.RangePicker
            value={operatedRange}
            onChange={range => setOperatedRange(range)}
            placeholder={['操作开始', '操作结束']}
          />
          <Button type="primary" onClick={handleSearch}>
            查询
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => refetch?.()}>
            刷新
          </Button>
        </Space>

        <Table<AuditLogRow>
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={rows}
          scroll={{ x: 1200 }}
          expandable={{
            expandedRowRender: record => (
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 480, overflow: 'auto' }}>
                {formatJsonDetail(record.detailJson)}
              </pre>
            ),
            rowExpandable: record => Boolean(record.detailJson),
          }}
          pagination={{
            current: page,
            pageSize,
            total: pageResult?.totalElements ?? 0,
            showSizeChanger: true,
            showTotal: total => `共 ${total} 条`,
            onChange: (p, size) => {
              setPage(p);
              setPageSize(size);
              fetchLogs(p, size);
            },
          }}
        />
      </Space>
    </Card>
  );
};

export default SettlementAuditLogPage;
